import React, { useState, useEffect } from 'react';
import { apiClient } from '../../../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_CARD } from './designTokens';

interface AvailableSeatRow {
  tableId: string;
  tableNumber: number;
  tableName: string;
  zoneName: string;
  zoneColor: string;
  totalSeats: number;
  available: number;
  occupied: number;
}

/**
 * AvailableSeatsView — Shows all tables that have open seats,
 * sorted by most available first. Includes a Print button.
 */
export function AvailableSeatsView() {
  const [data, setData] = useState<AvailableSeatRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient<AvailableSeatRow[]>('/table-management/reports/available-seats');
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Failed to load available seats report');
    } finally {
      setLoading(false);
    }
  };

  const totalOpen = data.reduce((sum, t) => sum + t.available, 0);
  const totalSeats = data.reduce((sum, t) => sum + t.totalSeats, 0);

  const handlePrint = () => {
    const rows = data.map((t) => `<tr>
      <td style="text-align:center;">${t.tableNumber}</td>
      <td>${t.tableName}</td>
      <td><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${t.zoneColor};margin-right:6px;vertical-align:middle;"></span>${t.zoneName}</td>
      <td style="text-align:center;font-weight:600;color:#1a6b3c;">${t.available}</td>
      <td style="text-align:center;">${t.totalSeats}</td>
      <td style="text-align:center;">${t.occupied}</td>
    </tr>`).join('');

    const html = `<!DOCTYPE html><html><head>
      <title>Available Seats Report</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 24px 32px; color: #1f2937; font-size: 13px; }
        h1 { font-size: 20px; font-weight: 700; margin-bottom: 4px; color: #0f2d1f; }
        p.sub { color: #6b7280; font-size: 12px; margin-bottom: 16px; }
        table { width: 100%; border-collapse: collapse; border: 1px solid #e5e7eb; }
        th { text-align: left; padding: 8px 10px; border: 1px solid #e5e7eb; font-size: 11px; font-weight: 600; background: #f3f4f6; color: #374151; text-transform: uppercase; letter-spacing: 0.3px; }
        td { padding: 6px 10px; border: 1px solid #e5e7eb; font-size: 12px; }
        tr:nth-child(even) { background: #f9fafb; }
        tfoot td { background: #f3f4f6; font-weight: 700; border-top: 2px solid #374151; }
        @media print { body { padding: 12px 16px; } }
      </style>
    </head><body>
      <h1>🪑 Available Seats Report</h1>
      <p class="sub">${data.length} tables with open seats &bull; ${totalOpen} available of ${totalSeats} total &bull; Printed ${new Date().toLocaleDateString()}</p>
      <table>
        <thead><tr>
          <th style="text-align:center;">Table #</th>
          <th>Table Name</th>
          <th>Zone</th>
          <th style="text-align:center;">Available</th>
          <th style="text-align:center;">Total</th>
          <th style="text-align:center;">Occupied</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr>
          <td colspan="3" style="text-align:right;">GRAND TOTAL (${data.length} tables)</td>
          <td style="text-align:center;">${totalOpen}</td>
          <td style="text-align:center;">${totalSeats}</td>
          <td style="text-align:center;">${totalSeats - totalOpen}</td>
        </tr></tfoot>
      </table>
    </body></html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: TEXT_MUTED }}>Loading available seats...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '1.5rem', textAlign: 'center', color: '#d32f2f' }}>
        <p>{error}</p>
        <button onClick={loadData} style={styles.retryBtn}>Retry</button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header with summary and print */}
      <div style={styles.header}>
        <div>
          <h3 style={styles.title}>🪑 Available Seats</h3>
          <p style={styles.subtitle}>
            {data.length} tables with open seats &bull; <strong>{totalOpen}</strong> available of {totalSeats} total
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={loadData} style={styles.refreshBtn} title="Refresh" aria-label="Refresh data">🔄</button>
          <button onClick={handlePrint} style={styles.printBtn} aria-label="Print available seats report">🖨️ Print</button>
        </div>
      </div>

      {data.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: TEXT_MUTED }}>
          All seats are currently assigned, held, or blocked. No tables have open seats.
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, textAlign: 'center', width: '60px' }}>Table #</th>
                <th style={styles.th}>Table Name</th>
                <th style={styles.th}>Zone</th>
                <th style={{ ...styles.th, textAlign: 'center', width: '80px' }}>Available</th>
                <th style={{ ...styles.th, textAlign: 'center', width: '60px' }}>Total</th>
                <th style={{ ...styles.th, textAlign: 'center', width: '70px' }}>Occupied</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.tableId} style={styles.tr}>
                  <td style={{ ...styles.td, textAlign: 'center', fontWeight: 600 }}>{row.tableNumber}</td>
                  <td style={styles.td}>{row.tableName}</td>
                  <td style={styles.td}>
                    <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '2px', backgroundColor: row.zoneColor, marginRight: '6px', verticalAlign: 'middle' }} />
                    {row.zoneName}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, color: PRIMARY }}>{row.available}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{row.totalSeats}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{row.occupied}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#f3f4f6' }}>
                <td colSpan={3} style={{ ...styles.td, fontWeight: 700, textAlign: 'right', borderTop: '2px solid #374151' }}>
                  TOTAL ({data.length} tables)
                </td>
                <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, color: PRIMARY, borderTop: '2px solid #374151' }}>{totalOpen}</td>
                <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, borderTop: '2px solid #374151' }}>{totalSeats}</td>
                <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, borderTop: '2px solid #374151' }}>{totalSeats - totalOpen}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: BG_CARD,
    borderRadius: '8px',
    border: `1px solid ${BORDER_LIGHT}`,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.25rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  title: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  subtitle: {
    fontSize: '0.82rem',
    color: TEXT_MUTED,
    margin: '2px 0 0',
  },
  printBtn: {
    padding: '6px 14px',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  refreshBtn: {
    padding: '6px 10px',
    fontSize: '0.85rem',
    backgroundColor: 'transparent',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    cursor: 'pointer',
  },
  retryBtn: {
    marginTop: '8px',
    padding: '6px 14px',
    fontSize: '0.82rem',
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  tableWrapper: {
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.82rem',
  },
  th: {
    padding: '8px 10px',
    fontSize: '0.72rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
    color: '#374151',
    backgroundColor: '#f9fafb',
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    textAlign: 'left' as const,
  },
  tr: {
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  td: {
    padding: '7px 10px',
    fontSize: '0.82rem',
    color: TEXT_DARK,
  },
};
